import {Button, Typography} from '@material-ui/core';
import {AsyncPage, Loaded, LoadingAdditional, matchAsyncPage} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import {none, some} from 'fp-ts/lib/Option';
import React, {ReactNode, useEffect, useState} from 'react';
import {AutoSizer, CellMeasurer, CellMeasurerCache, IndexRange, InfiniteLoader, List} from 'react-virtualized';
import 'Shared/components/CardPage.sass';
import ErrorPage from 'Shared/components/ErrorPage';
import FilterResultsDialog from 'Shared/components/FilterResultsDialog';
import Loading from 'Shared/components/Loading';
import {FilterFieldDefinitions} from 'Shared/components/Table/Table';

type CardPageProps<TData, TFilter> = {
  filterFields?: FilterFieldDefinitions<TFilter>;
  page: AsyncPage<Page<TData>>;
  filter?: TFilter;
  customHeader?: ReactNode;
  renderCard: (data: TData, style: any, key: any) => JSX.Element;
  getData: (page?: Page<TData>, filter?: TFilter, isFirstLoad?: boolean) => any;
};

let list: List | null = null;

const cache = new CellMeasurerCache({fixedWidth: true});

const CardsPage = <TData extends {}, TFilter extends {}>(props: CardPageProps<TData, TFilter>) => {
  const [showFilterModal, setShowFilterModal] = useState(false);

  useEffect(() => {
    if (list) {
      list.scrollToPosition(0);
      list.recomputeRowHeights();
    }
    cache.clearAll();
  }, [props.filter]);

  const result = matchAsyncPage(props.page, {
    Conflict: error => <ErrorPage error={error} />,
    Loaded: page => (
      <>
        {props.filterFields && props.filterFields.length > 0 && (
          <>
            <Button
              onClick={() => {
                setShowFilterModal(true);
                cache.clearAll();
                list!.recomputeRowHeights();
              } }
              fullWidth
            >
              <Typography style={{ paddingRight: '5px' }}>FILTER</Typography>
              <i className="fa fa-filter" />
            </Button>
          </>
        )}
        <div className="card-page__cards-container">
          <InfiniteLoader
            isRowLoaded={({ index }) => {
              return Boolean(page.result[index]);
            } }
            threshold={5}
            loadMoreRows={(indexParams: IndexRange) => {
              const shouldLoadMore = indexParams.stopIndex > page.result.length;
              if (shouldLoadMore) {
                props.getData(page, props.filter);
              }

              return Promise.resolve();
            } }
            rowCount={page.totalCount}
          >
            {({ onRowsRendered, registerChild }) => (
              <AutoSizer>
                {({ width, height }) => (
                  <List
                    noRowsRenderer={() => (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '100%',
                        }}
                      >
                        No Results Found
                      </div>
                    )}
                    deferredMeasurementCache={cache}
                    rowHeight={cache.rowHeight}
                    width={width}
                    height={height}
                    rowCount={page.result.length}
                    rowRenderer={({ index, parent, key, style }) => {
                      return page.result[index] ? (
                        <CellMeasurer cache={cache} columnIndex={0} key={key} rowIndex={index} parent={parent}>
                          {props.renderCard(page.result[index], style, key)}
                        </CellMeasurer>
                      ) : (
                        <div>Loading</div>
                      );
                    } }
                    onRowsRendered={onRowsRendered}
                    ref={l => {
                      registerChild(l);
                      list = l;
                    } } />
                )}
              </AutoSizer>
            )}
          </InfiniteLoader>
        </div>
        <div
          style={{
            display: 'flex',
            padding: '0 1em',
          }}
        >
          <span style={{ flexGrow: 1 }}>Total Results: {page.totalCount}</span>
          {props.page instanceof LoadingAdditional && (
            <span style={{ alignSelf: 'flex-end' }}>
              Loading <i className="fa fa-spinner fa-pulse" />
            </span>
          )}
        </div>
      </>
    ),
    Loading: () => <Loading />,
    ServerError: error => <ErrorPage error={error} />,
    Unauthorized: error => <ErrorPage error={error} />,
    NoData: error => <ErrorPage error={error} />,
    NoContent: error => <ErrorPage error={error} />,
    NotStarted: error => <ErrorPage error={error} />,
  });

  return (
    <>
      {props.customHeader}
      {showFilterModal && (
        <FilterResultsDialog
          filter={props.filter}
          filters={props.filterFields}
          onClose={() => setShowFilterModal(false)}
          totalResults={props.page instanceof Loaded ? some(props.page.Data.totalCount) : none}
        />
      )}
      {result}
    </>
  );
};

export default CardsPage;
